import { useState } from "react";
import { Link } from "react-router-dom";
import { Bus, Loader2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md rounded-lg border border-border bg-white p-6 sm:p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary">
            <Bus className="h-5 w-5 text-white" />
          </div>
          <h1 className="font-heading text-lg font-bold text-slate-900">Deluxe Travel Training</h1>
        </div>

        {done ? (
          <div data-testid="forgot-password-success" className="text-center py-4">
            <MailCheck className="mx-auto h-10 w-10 text-ukgreen mb-4" />
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Check your email</h2>
            <p className="text-sm text-slate-600 mb-6">
              If an account exists for <strong>{email}</strong>, a password reset link has been sent. The link is
              valid for 1 hour.
            </p>
            <Link to="/login" data-testid="back-to-login-link" className="text-sm font-medium text-primary hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">Reset your password</h2>
            <p className="text-sm text-slate-600 mb-6">
              Enter your staff email address and we will send you a secure reset link.
            </p>
            {error && (
              <div data-testid="forgot-password-error" role="alert" className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}
            <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div className="space-y-1.5">
                <Label htmlFor="reset-email">Email address</Label>
                <Input
                  id="reset-email"
                  data-testid="forgot-password-email-input"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <Button
                data-testid="forgot-password-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-primary hover:bg-brand-hover text-white font-semibold"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
            <div className="mt-4 text-sm">
              <Link to="/login" data-testid="back-to-login-link" className="font-medium text-primary hover:underline">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
